import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import arrayMutators from 'final-form-arrays';
import { uniqBy } from 'ramda';

import {
  getPublishRulesActiveTreeItem,
  getPublishRulesFormData,
  getPublishRulesFormRef,
  getPublishRulesHistoryChanges,
  getPublishRulesTree,
} from 'PublishRulesPackage/MainModule/selectors/publishRulesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { IKnmHistoryItemChanges } from 'KnmPackage/KnmHistoriesModule/interfaces/IKnmHistoryItemChanges';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import {
  getCatalogKnmStatuses248SelectOptions,
  getCatalogKnmStatuses294SelectOptions,
  getCatalogPmStatusesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { IPublishRules } from 'APIPackage/PublishRulesModule/interfaces/IPublishRules';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import {
  createOrUpdatePublishRules,
  deletePublishRules,
  loadPublishRulesData,
  setPublishRulesFormRef,
} from '../actions/publishRulesActions';
import { PUBLISH_RULES_FORM_ID } from '../constants/publishRulesFieldsElementsIds';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';
import { PublishRulesFormLeavingGuard } from './PublishRulesFormLeavingGuard';
import { PublishRulesFormAdditionalSettings } from './PublishRulesFormAdditionalSettings';
import { PublishRulesHistory } from './PublishRulesHistory';
import { makeBlockTitle } from '../utils/makeBlockTitle';
import { getPublishDeadlineOptions } from '../common/getPublishDeadlineOptions';
import { getKnmReasonType } from '../utils/getKnmReasonType';
import { PublishRulesApproveTypeEnum } from '../enums/publishRulesApproveTypeEnum';
import { makeUnplannedValues } from '../common/makeUnplannedValues';
import { IPublishRulesDecisionForm } from '../interfaces/IPublishRulesDecisionForm';
import { IPublishRulesDecisionFormRef } from '../interfaces/IPublishRulesDecisionFormRef';
import { getPublishRulesDecisionFormInitialValues } from '../common/getPublishRulesDecisionFormInitialValues';

interface IPublishRulesDecisionFormProps {
  activeTreeItem: IPublishRulesTreeItem;
  tree: IPublishRulesTreeItem[];
  formRef?: IPublishRulesDecisionFormRef;
  formData?: IPublishRules;
  historyChanges?: IKnmHistoryItemChanges[];
  pmStatusesSelectOptions?: IKnmStatusSelectOption[];
  knmStatusesSelectOptions?: IKnmStatusSelectOption[];
  erpStatusesSelectOptions?: IKnmStatusSelectOption[];
}

export type IPublishRulesDecisionFormRenderProps = FormRenderProps<IPublishRulesDecisionForm>;

const PublishRulesDecisionFormWithoutStore = ({
  activeTreeItem,
  tree,
  formRef,
  formData,
  historyChanges,
  pmStatusesSelectOptions,
  erpStatusesSelectOptions,
  knmStatusesSelectOptions,
}: IPublishRulesDecisionFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isConfirmDeleteSettingsVisible, setIsConfirmDeleteSettingsVisible] = useState<boolean>(false);
  const [reasonTypesSelectOptions, setReasonTypesSelectOptions] = useState<ISelectOption[]>([]);
  const [allowedStatusesSelectOptions, setAllowedStatusesSelectOptions] = useState<ISelectOption[]>([]);

  const setFormRef = useCallback(
    (newFormRef?: IPublishRulesDecisionFormRef) => dispatch(setPublishRulesFormRef(newFormRef)),
    [],
  );

  const allStatuses = useMemo(() => {
    if (pmStatusesSelectOptions && erpStatusesSelectOptions && knmStatusesSelectOptions) {
      const arr = [...pmStatusesSelectOptions, ...erpStatusesSelectOptions, ...knmStatusesSelectOptions];

      const uniqueByValue = uniqBy(item => item.value, arr);

      return uniqueByValue;
    }
  }, []);

  // Значения по умолчанию
  const initialValues = useMemo(() => getPublishRulesDecisionFormInitialValues(activeTreeItem, allStatuses), [
    activeTreeItem.category,
    allStatuses,
  ]);

  // Загрузка данных
  useEffect(() => {
    dispatch(loadPublishRulesData(activeTreeItem.category));
  }, [activeTreeItem.category]);

  // Инициализация полученными данными
  useEffect(() => {
    if (formData && formRef && allStatuses) {
      const { entries, ervkKnmReasonTypes, allowedStatusCodes } = formData;

      const reasonTypesSelectOptionsFromData = ervkKnmReasonTypes?.map(item => getKnmReasonType(item));
      const allowedStatuses = allStatuses.filter(item => allowedStatusCodes?.includes(item.code));

      reasonTypesSelectOptionsFromData && setReasonTypesSelectOptions(reasonTypesSelectOptionsFromData);
      allowedStatuses && setAllowedStatusesSelectOptions(allowedStatuses);

      if (entries?.length !== undefined && entries?.length > 0) {
        const entry = entries?.find(item => item.approveType === PublishRulesApproveTypeEnum.REQUIRED) || entries?.[0];

        const data: IPublishRulesDecisionForm = makeUnplannedValues(
          entry,
          allStatuses,
          reasonTypesSelectOptionsFromData || [],
        );

        formRef.current.initialize(data);
      }
    }
  }, [formData, formRef, allStatuses]);

  const blockTitle = useMemo(() => makeBlockTitle(tree, activeTreeItem), [tree, activeTreeItem]);

  const publishDeadlineSelectOptions = useMemo(() => getPublishDeadlineOptions(), []);

  const handleFormSubmit = useCallback(
    (data: IPublishRulesDecisionForm) => {
      const newData: IPublishRules = {
        entries: [
          {
            approveType: null,
            publicationCategoryCode: activeTreeItem.category,
            settings: {
              mainRule: {
                passportStatusCodes: data?.uponAttainingStatus?.map(item => item.value) || null,
                publicationMode: data?.publishDeadline?.value || null,
              },
              additionalRules:
                data?.additionalSettings?.map(setting => ({
                  reasonTypeId: (setting.typeOfBasis?.id as number) || null,
                  rule: {
                    passportStatusCodes: setting.uponAttainingStatus?.map(status => status.value) || null,
                    publicationMode: setting.publishDeadline?.value || null,
                  },
                })) || null,
            },
          },
        ],
      };

      dispatch(createOrUpdatePublishRules(activeTreeItem.category, newData));
    },
    [activeTreeItem.category],
  );

  const handleApplyDeleteSetting = useCallback(() => {
    dispatch(deletePublishRules(activeTreeItem.category, initialValues));
  }, [formRef, activeTreeItem.category, initialValues]);

  const handleOnClearSetting = useCallback(() => {
    setIsConfirmDeleteSettingsVisible(true);
  }, []);

  const renderForm = ({ handleSubmit, form }: FormRenderProps<IPublishRulesDecisionForm>) => {
    const currentFormRef = useRef<IPublishRulesDecisionFormRenderProps['form']>(form);

    useEffect(() => {
      if (currentFormRef) {
        setFormRef(currentFormRef);
      }
    }, [setFormRef, currentFormRef]);

    return (
      <form id={PUBLISH_RULES_FORM_ID} onSubmit={handleSubmit}>
        <KnmCollapse
          theme="fields"
          title={blockTitle}
          onClose={activeTreeItem.haveSavedSettings ? handleOnClearSetting : undefined}
          openByDefault
          isNew={!activeTreeItem.haveSavedSettings}
        >
          <>
            <FieldBlock label="Основное правило">
              <Row notWrap>
                <FieldBlock label="По достижении статуса" placementLabel="left" color="black" margin="right">
                  <SelectInputField
                    className="shared-row-field"
                    name="uponAttainingStatus"
                    isMulti
                    options={allowedStatusesSelectOptions}
                    placeholder="Выберите статус"
                    validate={makeRequired(REQUIRED_ERROR)}
                  />
                </FieldBlock>
                <FieldBlock label="Срок публикации" placementLabel="left" color="black">
                  <SelectInputField
                    className="shared-row-field"
                    name="publishDeadline"
                    placeholder="Выберите срок"
                    options={publishDeadlineSelectOptions}
                    validate={makeRequired(REQUIRED_ERROR)}
                  />
                </FieldBlock>
              </Row>
            </FieldBlock>
            <PublishRulesFormAdditionalSettings
              showDeadline
              showDate={false}
              statusesSelectOptions={allowedStatusesSelectOptions}
              reasonTypesSelectOptions={reasonTypesSelectOptions}
            />
            {historyChanges && (
              <Row>
                <PublishRulesHistory historyChanges={historyChanges} />
              </Row>
            )}
          </>
        </KnmCollapse>
        <PublishRulesFormLeavingGuard formRef={formRef} />
      </form>
    );
  };

  return (
    <>
      <Form<IPublishRulesDecisionForm>
        onSubmit={handleFormSubmit}
        render={renderForm}
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
      />
      <ConfirmModal
        visible={isConfirmDeleteSettingsVisible}
        title="Подтверждение"
        text="Удалить текущие настройки?"
        onApply={handleApplyDeleteSetting}
        onClose={() => setIsConfirmDeleteSettingsVisible(false)}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  activeTreeItem: getPublishRulesActiveTreeItem(state),
  tree: getPublishRulesTree(state),
  formRef: getPublishRulesFormRef(state),
  formData: getPublishRulesFormData(state),
  historyChanges: getPublishRulesHistoryChanges(state),
  pmStatusesSelectOptions: getCatalogPmStatusesSelectOptions(state),
  erpStatusesSelectOptions: getCatalogKnmStatuses294SelectOptions(state),
  knmStatusesSelectOptions: getCatalogKnmStatuses248SelectOptions(state),
});

export const PublishRulesDecisionForm = connect(mapStateToProps)(PublishRulesDecisionFormWithoutStore);
